import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Form, Input, Select, Modal, message, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../utils/api';

const { Option } = Select;

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const deptsRes = await axios.get(`${API_BASE_URL}/api/departments`, { headers });
      setDepartments(deptsRes.data);
    } catch (err) {
      setError('Failed to load departments');
      message.error('Failed to load departments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (department) => {
    setEditingDepartment(department);
    setModalVisible(true);
    form.setFieldsValue({
      name: department.name,
      description: department.description,
      access_level: department.access_level
    });
  };

  const handleDelete = async (departmentId) => {
    Modal.confirm({
      title: 'Delete Department',
      content: 'Are you sure you want to delete this department?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_BASE_URL}/api/departments/${departmentId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          message.success('Department deleted');
          fetchData();
        } catch (err) {
          message.error('Failed to delete department');
        }
      }
    });
  };

  const handleAdd = () => {
    setEditingDepartment(null);
    setModalVisible(true);
    form.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editingDepartment) {
        await axios.put(
          `${API_BASE_URL}/api/departments/${editingDepartment.id}`,
          values,
          { headers }
        );
        message.success('Department updated');
      } else {
        await axios.post(`${API_BASE_URL}/api/departments`, values, { headers });
        message.success('Department created');
      }
      setModalVisible(false);
      fetchData();
    } catch (err) {
      message.error('Failed to save department');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => <span style={{ color: '#888' }}>{text}</span>
    },
    {
      title: 'Access Level',
      dataIndex: 'access_level',
      key: 'access_level',
      render: (level) => {
        const levels = { 1: 'Low', 2: 'Medium', 3: 'High' };
        return levels[level] || 'Low';
      }
    },
    {
      title: 'User Count',
      dataIndex: 'user_count',
      key: 'user_count',
      render: (count) => count || 0
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, department) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(department)} />
          <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(department.id)} />
        </Space>
      )
    }
  ];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <Card
        title="Departments"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>Add Department</Button>}
        style={{ marginBottom: 24 }}
      >
        <Table
          columns={columns}
          dataSource={departments}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 8 }}
        />
      </Card>

      <Modal
        title={editingDepartment ? 'Edit Department' : 'Add Department'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        okText={editingDepartment ? 'Update' : 'Create'}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ name: '', description: '', access_level: 1 }}
        >
          <Form.Item
            name="name"
            label="Department Name"
            rules={[{ required: true, message: 'Please enter department name' }]}
          >
            <Input placeholder="Enter department name" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={3} placeholder="Enter description" />
          </Form.Item>
          <Form.Item
            name="access_level"
            label="Access Level"
            rules={[{ required: true, message: 'Please select access level' }]}
          >
            <Select placeholder="Select access level">
              <Option value={1}>Low</Option>
              <Option value={2}>Medium</Option>
              <Option value={3}>High</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Departments; 